import { useCallback, useState, useMemo } from 'react';

import {
  RECOMMENDED_CONTENT_TYPES,
  ContentRecommendationList,
} from '@hivebrite/design-system/ContentRecommendation';
import type { ContentRecommendationType } from '@hivebrite/design-system/ContentRecommendation';
import { Divider } from '@hivebrite/design-system/Divider';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useNavigateToNewsDetail } from 'hb-react/frontoffice/apps/NewsIndex/hooks/useNavigateToNewsDetail';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useGetContentRecommendations } from 'hb-react/shared/components/ContentRecommendation/hooks/useGetContentRecommendation';
import { usePostReportContent } from 'hb-react/shared/components/ContentRecommendation/hooks/usePostReportContent';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

interface ContentRecommendationProps {
  contentId: number;
  contentType: RECOMMENDED_CONTENT_TYPES;
}

export const ContentRecommendation = ({
  contentId,
  contentType,
}: ContentRecommendationProps) => {
  const { flagUjAiContentRecommendationReport } = useFeatureFlag();
  const { topicId } = useTopicContext();
  const { onClick: navigateToNews } = useNavigateToNewsDetail();
  const { mutate: reportContent } = usePostReportContent();
  const { data: contentItems, isLoading } = useGetContentRecommendations({
    contentId,
    topicId,
    type: contentType,
  });

  const [reportedContentIds, setReportedContentIds] = useState<Array<number>>(
    [],
  );

  const filteredContentItems = useMemo(
    () => contentItems.filter((item) => !reportedContentIds.includes(item.id)),
    [contentItems, reportedContentIds],
  );

  const handleClickOnRecommendedItem = useCallback(
    (recommendedContent: ContentRecommendationType) => {
      switch (recommendedContent.contentType) {
        case RECOMMENDED_CONTENT_TYPES.NEWS:
          navigateToNews(recommendedContent.id);
          break;
        default:
          break;
      }
    },
    [navigateToNews],
  );

  const handleReportContent = useCallback(
    (contentIdToReport: number) => {
      reportContent(
        {
          contentIdToReport,
        },
        {
          onSuccess: () => {
            setReportedContentIds((prev) => [...prev, contentIdToReport]);
          },
        },
      );
    },
    [reportContent],
  );

  const getItemActions = useCallback(
    (item: ContentRecommendationType) => {
      if (!flagUjAiContentRecommendationReport) {
        return [];
      }

      return [
        {
          label: s__('ContentRecommendation|Help us address this content'),
          onClick: () => handleReportContent(item.id),
          icon: () => (
            <FontAwesomeIcon
              height="16px"
              iconName="flag"
              iconType={CATEGORIES.REGULAR}
            />
          ),
        },
      ];
    },
    [flagUjAiContentRecommendationReport, handleReportContent],
  );

  if (!isLoading && filteredContentItems.length === 0) {
    return null;
  }

  return (
    <>
      <Divider mb={0} />
      <ContentRecommendationList
        contentItems={filteredContentItems}
        isLoading={isLoading}
        getActions={getItemActions}
        onItemClick={handleClickOnRecommendedItem}
      />
    </>
  );
};
